<script setup lang="ts">
/**
 * Time bar chart (Gantt): dated bars in rows over a day axis — a project plan
 * with phases, tasks and milestones.
 *
 * Axis: one column per day from `start` to `end` (both inclusive). Up to six
 * weeks the header names every day under its calendar week; longer ranges
 * label weeks under their month (`scale` forces either). The bars stay
 * day-exact in both. Weekends — or what `nonWorking` says — and the caller's
 * `holidays` are hatched, today gets a line. Without `start`/`end` the range
 * fits the data: whole weeks with two days of air on either side.
 *
 * Rows can sit under group headers (the phases of a project): `groups` in
 * their order, a row joins one through `groupId`; rows without a known group
 * come first. A group header shows the span of everything under it and, with
 * `collapsible`, folds its rows away (`v-model:collapsed`).
 *
 * Items are bars in their row, coloured by `tone` (intent) or `hue`
 * (category), with their `text` inside; `delayed` draws the red „Verzug“
 * frame. Overlapping items of one row stack in lanes. A bar reaching past the
 * visible range is cut square at that edge and shows an arrow. Milestones are
 * diamonds on a row or on a group header; `missed` turns one red, `done`
 * green. `legend` explains the caller's colours under the chart.
 *
 * The chart judges nothing — what is late and which milestone is missed is
 * the caller's call — and moves nothing: no drag, no resize, the plan changes
 * where its dates are edited. A click (or Enter/Space) on a bar or a diamond
 * only emits `open` / `milestone-open`.
 *
 * Keyboard: bars and diamonds are buttons in reading order. Left/Right walk
 * the row, Up/Down jump to the nearest mark in the next row that has one,
 * Home/End go to the row's first/last mark. Every mark carries its full
 * accessible name („Datenmapping, 01.10.–05.10.2026, In Arbeit, Verzug“); the
 * visual date header is hidden from assistive technology.
 */
import { computed, inject, ref } from 'vue';
import { CaretLeftIcon, CaretRightIcon, ChevronDownIcon, ChevronRightIcon } from '@blpde/nx-icons';
import NxBadge from '../Badge/NxBadge.vue';
import NxEmptyState from '../EmptyState/NxEmptyState.vue';
import { todayIso } from '../Inputs/dateRangePresets';
import { i18nInjectionKey } from '../internal';
import { hueVars } from '../lib/hue';
import {
  buildTimeBarAxis,
  formatTimeBarRange,
  groupTimeBarRows,
  placeTimeBarItems,
  placeTimeBarMilestones,
  resolveTimeBarRange,
  timeBarExtent,
  timeBarFocusTarget,
  timeBarItemLabel,
  timeBarMilestoneLabel,
  toIsoDay,
  type NxTimeBarGroup,
  type NxTimeBarItem,
  type NxTimeBarKey,
  type NxTimeBarLegendEntry,
  type NxTimeBarMilestone,
  type NxTimeBarRow,
  type NxTimeBarScale,
  type TimeBarExtent,
  type TimeBarPlacedItem,
  type TimeBarPlacedMilestone,
  type TimeBarSpan,
} from './timeBar';

const props = withDefaults(
  defineProps<{
    /** Rows in display order. */
    rows: NxTimeBarRow[];
    /** Group headers in display order; a row joins one through `groupId`. */
    groups?: NxTimeBarGroup[];
    items?: NxTimeBarItem[];
    milestones?: NxTimeBarMilestone[];
    /** First visible day (ISO). Omitted: fitted to the data. */
    start?: string;
    /** Last visible day (ISO), inclusive. Omitted: fitted to the data. */
    end?: string;
    /** Header unit. Omitted: days up to six weeks, weeks above. */
    scale?: NxTimeBarScale;
    /** Today (ISO) for the today line. Default: the local date. */
    today?: string;
    /**
     * Public holidays, ISO day → name: hatched, named in the header tooltip.
     * The kit knows no holidays — which days count is the caller's business.
     */
    holidays?: Readonly<Record<string, string>>;
    /** Replaces the weekend rule (Saturday and Sunday) for non-working days. */
    nonWorking?: (iso: string) => boolean;
    /** Group headers get a toggle that folds their rows away. */
    collapsible?: boolean;
    /** Key under the chart: the caller names what its colours mean. */
    legend?: NxTimeBarLegendEntry[];
    /** Date formats. Falls back to the host app's i18n locale, then `de-DE`. */
    locale?: string;
    ariaLabel?: string;
    /** Caption of the row header column. */
    rowHeader?: string;
    /** Width of the row header column (CSS length). */
    rowHeaderWidth?: string;
    /** Minimum width of one day (CSS length). Default: roomy for days, narrow for weeks. */
    dayWidth?: string;
    /** Header label of a calendar week. */
    weekLabel?: (week: number) => string;
    /** Tooltip of today's column. */
    todayLabel?: string;
    /** Accessible suffix of a delayed bar. */
    delayedLabel?: string;
    milestoneLabel?: string;
    missedLabel?: string;
    doneLabel?: string;
    /** Accessible name of the date column; the visible range follows it. */
    periodLabel?: string;
    legendLabel?: string;
    /** Shown when there are neither rows nor groups (the `empty` slot overrides it). */
    emptyLabel?: string;
  }>(),
  {
    groups: () => [],
    items: () => [],
    milestones: () => [],
    start: undefined,
    end: undefined,
    scale: undefined,
    today: undefined,
    holidays: undefined,
    nonWorking: undefined,
    collapsible: false,
    legend: () => [],
    locale: undefined,
    ariaLabel: 'Zeitachse',
    rowHeader: '',
    rowHeaderWidth: 'calc(var(--nxt-size-unit) * 14)',
    dayWidth: undefined,
    weekLabel: (week: number) => `KW ${week}`,
    todayLabel: 'Heute',
    delayedLabel: 'Verzug',
    milestoneLabel: 'Meilenstein',
    missedLabel: 'verpasst',
    doneLabel: 'erledigt',
    periodLabel: 'Zeitraum',
    legendLabel: 'Legende',
    emptyLabel: 'Keine terminierten Vorgänge',
  }
);

const emit = defineEmits<{
  open: [item: NxTimeBarItem];
  'milestone-open': [milestone: NxTimeBarMilestone];
}>();

/** Ids of the folded groups. */
const collapsed = defineModel<NxTimeBarKey[]>('collapsed', { default: () => [] });

defineSlots<{
  'row-label'?(props: { row: NxTimeBarRow }): unknown;
  'group-label'?(props: { group: NxTimeBarGroup; collapsed: boolean }): unknown;
  /** Content of a bar; the cut arrows stay outside it. */
  item?(props: { item: NxTimeBarItem; clippedStart: boolean; clippedEnd: boolean }): unknown;
  empty?(): unknown;
}>();

/** Grid line of the first body row: the two header rows come first. */
const FIRST_BODY_LINE = 3;
const DAY_WIDTH = 'calc(var(--nxt-size-unit) * 1.75)';
const WEEK_DAY_WIDTH = 'calc(var(--nxt-size-unit) * 0.5)';
const NAV_KEYS = new Set(['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End']);

const gridRef = ref<HTMLElement | null>(null);
const i18n = inject(i18nInjectionKey, null);

const resolvedLocale = computed(
  () => props.locale ?? (i18n?.locale?.value as string | undefined) ?? 'de-DE'
);
const todayValue = computed(() => toIsoDay(props.today) ?? todayIso());

const axis = computed(() => {
  const range = resolveTimeBarRange({
    start: props.start,
    end: props.end,
    items: props.items,
    milestones: props.milestones,
    today: todayValue.value,
  });
  return buildTimeBarAxis({
    ...range,
    scale: props.scale,
    today: todayValue.value,
    holidays: props.holidays,
    nonWorking: props.nonWorking,
    locale: resolvedLocale.value,
    weekLabel: props.weekLabel,
  });
});

type Mark =
  | { kind: 'item'; key: string; column: number; label: string; placed: TimeBarPlacedItem }
  | {
      kind: 'milestone';
      key: string;
      column: number;
      label: string;
      placed: TimeBarPlacedMilestone;
    };

type Entry =
  | {
      kind: 'row';
      key: string;
      row: NxTimeBarRow;
      /** First grid line; the row takes `lanes` lines. */
      line: number;
      lanes: number;
      marks: Mark[];
    }
  | {
      kind: 'group';
      key: string;
      group: NxTimeBarGroup;
      line: number;
      lanes: 1;
      marks: Mark[];
      collapsed: boolean;
      extent: TimeBarExtent | null;
      extentLabel: string;
    };

function bucket<T>(list: readonly T[], keyOf: (entry: T) => NxTimeBarKey | undefined) {
  const map = new Map<NxTimeBarKey, T[]>();
  for (const entry of list) {
    const key = keyOf(entry);
    if (key == null) continue;
    const found = map.get(key);
    if (found) found.push(entry);
    else map.set(key, [entry]);
  }
  return map;
}

const layout = computed(() => {
  const { start, days } = axis.value;
  const dayCount = days.length;
  const locale = resolvedLocale.value;
  const itemsByRow = bucket(props.items, (item) => item.rowId);
  const milestonesByRow = bucket(props.milestones, (milestone) => milestone.rowId);
  const milestonesByGroup = bucket(props.milestones, (milestone) =>
    milestone.rowId == null ? milestone.groupId : undefined
  );
  const milestoneLabels = {
    milestone: props.milestoneLabel,
    missed: props.missedLabel,
    done: props.doneLabel,
  };

  const milestoneMarks = (list: NxTimeBarMilestone[] = []): Mark[] =>
    placeTimeBarMilestones(list, start, dayCount).map((placed) => ({
      kind: 'milestone',
      key: `m:${placed.milestone.id}`,
      column: placed.column,
      label: timeBarMilestoneLabel(placed.milestone, placed.date, locale, milestoneLabels),
      placed,
    }));

  const entries: Entry[] = [];
  let line = FIRST_BODY_LINE;
  for (const section of groupTimeBarRows(props.rows, props.groups)) {
    const { group } = section;
    if (group) {
      const folded = collapsed.value.includes(group.id);
      const own = milestonesByGroup.get(group.id) ?? [];
      const extent = timeBarExtent(
        section.rows.flatMap((row) => itemsByRow.get(row.id) ?? []),
        [...section.rows.flatMap((row) => milestonesByRow.get(row.id) ?? []), ...own],
        start,
        dayCount
      );
      entries.push({
        kind: 'group',
        key: `g:${group.id}`,
        group,
        line,
        lanes: 1,
        marks: milestoneMarks(own),
        collapsed: folded,
        extent,
        extentLabel: extent ? formatTimeBarRange(extent.start, extent.end, locale) : '',
      });
      line += 1;
      if (folded) continue;
    }
    for (const row of section.rows) {
      const { placed, lanes } = placeTimeBarItems(itemsByRow.get(row.id) ?? [], start, dayCount);
      const marks: Mark[] = [
        ...placed.map(
          (entry): Mark => ({
            kind: 'item',
            key: `i:${entry.item.id}`,
            column: entry.column,
            label: timeBarItemLabel(entry.item, entry, locale, props.delayedLabel),
            placed: entry,
          })
        ),
        ...milestoneMarks(milestonesByRow.get(row.id)),
      ];
      // Stable: on the same day the bar comes before the diamond.
      marks.sort((a, b) => a.column - b.column);
      entries.push({ kind: 'row', key: `r:${row.id}`, row, line, lanes, marks });
      line += lanes;
    }
  }
  return { entries, bodyLines: Math.max(1, line - FIRST_BODY_LINE) };
});

const gridStyle = computed(() => {
  const dayCount = axis.value.days.length;
  const dayWidth = props.dayWidth ?? (axis.value.scale === 'week' ? WEEK_DAY_WIDTH : DAY_WIDTH);
  return {
    gridTemplateColumns: `${props.rowHeaderWidth} repeat(${dayCount}, minmax(${dayWidth}, 1fr))`,
    // An explicit floor instead of `max-content`: under a max-content width a
    // long bar text would widen its fr share and with it every day column.
    minWidth: `calc(${props.rowHeaderWidth} + ${dayCount} * ${dayWidth})`,
  };
});

/** The body lines the day backdrop (non-working days, week lines, today) covers. */
const bodyRows = computed(() => `${FIRST_BODY_LINE} / span ${layout.value.bodyLines}`);

const periodText = computed(
  () =>
    `${props.periodLabel}: ${formatTimeBarRange(axis.value.start, axis.value.end, resolvedLocale.value)}`
);

function columns(start: number, span: number): string {
  return `${start + 2} / span ${span}`;
}

function lines(entry: Entry): string {
  return `${entry.line} / span ${entry.lanes}`;
}

function unitTitle(unit: TimeBarSpan): string | undefined {
  const parts: string[] = [];
  if (unit.current && axis.value.scale === 'day') parts.push(props.todayLabel);
  if (unit.title) parts.push(unit.title);
  return parts.length ? parts.join(' · ') : undefined;
}

function accentClasses(tone: string | undefined, hue: string | undefined, fallback: string) {
  return [`tone-${tone ?? fallback}`, { 'has-hue': !!hue }];
}

function itemClasses(placed: TimeBarPlacedItem) {
  return [
    ...accentClasses(placed.item.tone, placed.item.hue, 'brand'),
    {
      'is-delayed': !!placed.item.delayed,
      'is-clipped-start': placed.clippedStart,
      'is-clipped-end': placed.clippedEnd,
    },
  ];
}

function itemStyle(entry: Entry, placed: TimeBarPlacedItem) {
  return {
    gridColumn: columns(placed.column, placed.span),
    gridRow: String(entry.line + placed.lane),
    ...hueVars(placed.item.hue),
  };
}

function milestoneClasses(milestone: NxTimeBarMilestone) {
  return { 'is-missed': !!milestone.missed, 'is-done': !milestone.missed && !!milestone.done };
}

function swatchClasses(entry: NxTimeBarLegendEntry) {
  const kind = entry.kind ?? 'bar';
  return kind === 'bar'
    ? ['kind-bar', ...accentClasses(entry.tone, entry.hue, 'brand')]
    : [`kind-${kind}`];
}

function onGroupClick(id: NxTimeBarKey) {
  if (!props.collapsible) return;
  collapsed.value = collapsed.value.includes(id)
    ? collapsed.value.filter((key) => key !== id)
    : [...collapsed.value, id];
}

function onMarkKeydown(event: KeyboardEvent) {
  const mark = event.currentTarget as HTMLElement;
  if (event.key === 'Enter') {
    // One path for pointer, Enter and Space: the button's click. Taking Enter
    // over also keeps the native activation from opening a second time.
    event.preventDefault();
    mark.click();
    return;
  }
  if (!NAV_KEYS.has(event.key) || event.altKey || event.ctrlKey || event.metaKey) return;
  const stops = Array.from(
    gridRef.value?.querySelectorAll<HTMLElement>('[data-time-bar-stop]') ?? []
  );
  const from = stops.indexOf(mark);
  if (from < 0) return;
  // Arrows never scroll the chart from a mark, whether or not there is a next one.
  event.preventDefault();
  const to = timeBarFocusTarget(
    stops.map((stop) => ({ row: stop.dataset.row ?? '', column: Number(stop.dataset.column) })),
    from,
    event.key
  );
  if (to !== null) stops[to].focus();
}
</script>

<template>
  <div class="nx-time-bar" :class="`scale-${axis.scale}`">
    <div class="nx-time-bar__scroll" :style="{ '--time-bar-head-width': rowHeaderWidth }">
      <div
        ref="gridRef"
        class="nx-time-bar__grid"
        role="table"
        :aria-label="ariaLabel"
        :style="gridStyle"
      >
        <div class="nx-time-bar__row" role="row">
          <div class="nx-time-bar__corner" role="columnheader">{{ rowHeader }}</div>
          <div class="nx-time-bar__sr-only" role="columnheader">{{ periodText }}</div>
        </div>

        <!-- Date header: decoration, every mark names its own dates. -->
        <div
          v-for="band in axis.bands"
          :key="`b:${band.key}`"
          class="nx-time-bar__band"
          :class="{ 'is-current': band.current }"
          :style="{ gridColumn: columns(band.start, band.span) }"
          aria-hidden="true"
        >
          <span class="nx-time-bar__band-label">{{ band.label }}</span>
          <span v-if="band.sublabel" class="nx-time-bar__band-sub">{{ band.sublabel }}</span>
        </div>
        <div
          v-for="unit in axis.units"
          :key="`u:${unit.key}`"
          class="nx-time-bar__unit"
          :class="{ 'is-current': unit.current, 'is-non-working': unit.nonWorking }"
          :title="unitTitle(unit)"
          :style="{ gridColumn: columns(unit.start, unit.span) }"
          aria-hidden="true"
        >
          <span class="nx-time-bar__unit-label">{{ unit.label }}</span>
          <span class="nx-time-bar__unit-sub">{{ unit.sublabel }}</span>
        </div>

        <!-- Day backdrop behind the rows. -->
        <div
          v-for="run in axis.nonWorkingRuns"
          :key="`o:${run.start}`"
          class="nx-time-bar__off"
          :style="{ gridColumn: columns(run.start, run.span), gridRow: bodyRows }"
          aria-hidden="true"
        />
        <div
          v-for="column in axis.weekStarts"
          :key="`w:${column}`"
          class="nx-time-bar__week-line"
          :style="{ gridColumn: columns(column, 1), gridRow: bodyRows }"
          aria-hidden="true"
        />
        <div
          v-if="axis.todayIndex !== null"
          class="nx-time-bar__today"
          :style="{ gridColumn: columns(axis.todayIndex, 1), gridRow: bodyRows }"
          aria-hidden="true"
        />

        <div v-for="entry in layout.entries" :key="entry.key" class="nx-time-bar__row" role="row">
          <div
            v-if="entry.kind === 'group'"
            class="nx-time-bar__group-head"
            :class="[
              ...accentClasses(entry.group.tone, entry.group.hue, 'neutral'),
              { 'has-accent': !!(entry.group.tone || entry.group.hue) },
            ]"
            :style="{ gridRow: lines(entry), ...hueVars(entry.group.hue) }"
            role="rowheader"
          >
            <component
              :is="collapsible ? 'button' : 'div'"
              class="nx-time-bar__group-main"
              :class="{ 'is-toggle': collapsible }"
              :type="collapsible ? 'button' : undefined"
              :aria-expanded="collapsible ? !entry.collapsed : undefined"
              @click="onGroupClick(entry.group.id)"
            >
              <template v-if="collapsible">
                <ChevronRightIcon
                  v-if="entry.collapsed"
                  filled
                  class="nxt-icon-sm nx-time-bar__chevron"
                  aria-hidden="true"
                />
                <ChevronDownIcon
                  v-else
                  filled
                  class="nxt-icon-sm nx-time-bar__chevron"
                  aria-hidden="true"
                />
              </template>
              <span class="nx-time-bar__group-text">
                <slot name="group-label" :group="entry.group" :collapsed="entry.collapsed">
                  <span class="nx-time-bar__group-label" :title="entry.group.label">
                    {{ entry.group.label }}
                  </span>
                  <span v-if="entry.group.sublabel" class="nx-time-bar__sub">
                    {{ entry.group.sublabel }}
                  </span>
                </slot>
              </span>
            </component>
          </div>
          <div
            v-else
            class="nx-time-bar__row-head"
            :style="{ gridRow: lines(entry) }"
            role="rowheader"
          >
            <slot name="row-label" :row="entry.row">
              <span class="nx-time-bar__row-line">
                <NxBadge v-if="entry.row.tag" variant="neutral" size="xs" class="nx-time-bar__tag">
                  {{ entry.row.tag }}
                </NxBadge>
                <span class="nx-time-bar__row-label" :title="entry.row.label">
                  {{ entry.row.label }}
                </span>
              </span>
              <span v-if="entry.row.sublabel" class="nx-time-bar__sub">
                {{ entry.row.sublabel }}
              </span>
            </slot>
          </div>

          <div
            class="nx-time-bar__track"
            :class="{ 'is-group': entry.kind === 'group' }"
            :style="{ gridRow: lines(entry) }"
            aria-hidden="true"
          />

          <div class="nx-time-bar__cell" role="cell">
            <template v-if="entry.kind === 'group' && entry.extent">
              <div
                class="nx-time-bar__summary"
                :class="[
                  ...accentClasses(entry.group.tone, entry.group.hue, 'neutral'),
                  {
                    'is-clipped-start': entry.extent.clippedStart,
                    'is-clipped-end': entry.extent.clippedEnd,
                  },
                ]"
                :style="{
                  gridColumn: columns(entry.extent.column, entry.extent.span),
                  gridRow: lines(entry),
                  ...hueVars(entry.group.hue),
                }"
                :title="entry.extentLabel"
                aria-hidden="true"
              />
              <span class="nx-time-bar__sr-only">{{ entry.extentLabel }}</span>
            </template>

            <template v-for="mark in entry.marks" :key="mark.key">
              <button
                v-if="mark.kind === 'item'"
                type="button"
                class="nx-time-bar__item"
                :class="itemClasses(mark.placed)"
                :style="itemStyle(entry, mark.placed)"
                :aria-label="mark.label"
                :title="mark.label"
                data-time-bar-stop
                :data-row="entry.key"
                :data-column="mark.column"
                @click="emit('open', mark.placed.item)"
                @keydown="onMarkKeydown"
              >
                <CaretLeftIcon
                  v-if="mark.placed.clippedStart"
                  filled
                  class="nxt-icon-xs nx-time-bar__cut"
                  aria-hidden="true"
                />
                <span class="nx-time-bar__item-text">
                  <slot
                    name="item"
                    :item="mark.placed.item"
                    :clipped-start="mark.placed.clippedStart"
                    :clipped-end="mark.placed.clippedEnd"
                  >
                    {{ mark.placed.item.text ?? mark.placed.item.label }}
                  </slot>
                </span>
                <CaretRightIcon
                  v-if="mark.placed.clippedEnd"
                  filled
                  class="nxt-icon-xs nx-time-bar__cut"
                  aria-hidden="true"
                />
              </button>
              <button
                v-else
                type="button"
                class="nx-time-bar__milestone"
                :class="milestoneClasses(mark.placed.milestone)"
                :style="{ gridColumn: columns(mark.column, 1), gridRow: lines(entry) }"
                :aria-label="mark.label"
                :title="mark.label"
                data-time-bar-stop
                :data-row="entry.key"
                :data-column="mark.column"
                @click="emit('milestone-open', mark.placed.milestone)"
                @keydown="onMarkKeydown"
              />
            </template>
          </div>
        </div>

        <div v-if="!layout.entries.length" class="nx-time-bar__row" role="row">
          <div
            class="nx-time-bar__empty"
            :style="{ gridRow: String(FIRST_BODY_LINE) }"
            role="cell"
            aria-colspan="2"
          >
            <div class="nx-time-bar__empty-inner">
              <slot name="empty">
                <NxEmptyState compact :title="emptyLabel" />
              </slot>
            </div>
          </div>
        </div>
      </div>
    </div>

    <ul v-if="legend.length" class="nx-time-bar__legend" role="list" :aria-label="legendLabel">
      <li v-for="(entry, index) in legend" :key="index">
        <span
          class="nx-time-bar__swatch"
          :class="swatchClasses(entry)"
          :style="(entry.kind ?? 'bar') === 'bar' ? hueVars(entry.hue) : undefined"
          aria-hidden="true"
        />
        {{ entry.label }}
      </li>
    </ul>
  </div>
</template>

<style scoped>
.nx-time-bar {
  --time-bar-lane: var(--nxt-control-size-md);
  --time-bar-band-height: var(--nxt-control-size-xs);
  --time-bar-unit-height: var(--nxt-control-size-lg);
  --time-bar-hatch: repeating-linear-gradient(
    135deg,
    color-mix(in srgb, var(--nxt-border-strong) 70%, transparent) 0 var(--nxt-hairline),
    transparent var(--nxt-hairline) calc(var(--nxt-size-unit) * 0.375)
  );
  position: relative;
  display: flex;
  flex-direction: column;
  gap: var(--nxt-space-xs);
  min-width: 0;
  min-height: 0;
}

/* Takes the height a constrained root leaves, so long plans scroll under the
   sticky header. The scroll padding keeps a focused bar clear of both sticky
   areas when the keyboard scrolls it into view. */
.nx-time-bar__scroll {
  flex: 1 1 auto;
  min-height: 0;
  overflow: auto;
  scroll-padding-block-start: calc(var(--time-bar-band-height) + var(--time-bar-unit-height));
  scroll-padding-inline-start: var(--time-bar-head-width);
  border: var(--nxt-hairline) solid var(--nxt-border);
  border-radius: var(--nxt-radius-md);
  background: var(--nxt-surface-bg);
}

.nx-time-bar__grid {
  position: relative;
  display: grid;
  grid-template-rows: var(--time-bar-band-height) var(--time-bar-unit-height);
  grid-auto-rows: minmax(var(--time-bar-lane), auto);
  font-size: var(--nxt-font-size-xs);
  color: var(--nxt-fg);
}

/* Rows and cells exist for the accessibility tree only; their children sit on the grid. */
.nx-time-bar__row,
.nx-time-bar__cell {
  display: contents;
}

/* ── Header ────────────────────────────────────────────────────────────────── */
.nx-time-bar__corner,
.nx-time-bar__band,
.nx-time-bar__unit {
  position: sticky;
  z-index: calc(var(--nxt-z-raised) + 2);
  min-width: 0;
  overflow: hidden;
  background: var(--nxt-surface-raised-bg);
  color: var(--nxt-fg-muted);
  white-space: nowrap;
}
.nx-time-bar__corner {
  top: 0;
  left: 0;
  z-index: calc(var(--nxt-z-raised) + 4);
  grid-column: 1;
  grid-row: 1 / span 2;
  display: flex;
  align-items: flex-end;
  padding: var(--nxt-space-xs) var(--nxt-space-sm);
  border-right: var(--nxt-hairline) solid var(--nxt-border);
  border-bottom: var(--nxt-hairline) solid var(--nxt-border);
  font-weight: var(--nxt-font-weight-semibold);
}
.nx-time-bar__band {
  top: 0;
  grid-row: 1;
  display: flex;
  align-items: center;
  gap: var(--nxt-space-2xs);
  padding: 0 var(--nxt-space-xs);
  border-left: var(--nxt-hairline) solid var(--nxt-border-subtle);
  border-bottom: var(--nxt-hairline) solid var(--nxt-border-subtle);
}
.nx-time-bar__band-label {
  font-weight: var(--nxt-font-weight-semibold);
  color: var(--nxt-fg-subtle);
}
.nx-time-bar__band-sub {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  font-variant-numeric: tabular-nums;
}
.nx-time-bar__unit {
  top: var(--time-bar-band-height);
  grid-row: 2;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  line-height: var(--nxt-line-height-tight);
  border-left: var(--nxt-hairline) solid var(--nxt-border-subtle);
  border-bottom: var(--nxt-hairline) solid var(--nxt-border);
  font-variant-numeric: tabular-nums;
}
/* A day reads weekday over date („Mo“ / „28“), a week its number over its Monday. */
.scale-day .nx-time-bar__unit {
  flex-direction: column-reverse;
}
.nx-time-bar__unit-label {
  font-weight: var(--nxt-font-weight-semibold);
  color: var(--nxt-fg-subtle);
}
.nx-time-bar__unit.is-non-working {
  background-image: var(--time-bar-hatch);
}
.nx-time-bar__band.is-current,
.nx-time-bar__band.is-current .nx-time-bar__band-label,
.nx-time-bar__unit.is-current,
.nx-time-bar__unit.is-current .nx-time-bar__unit-label {
  color: var(--nxt-brand-bg);
}
.nx-time-bar__unit.is-current {
  box-shadow: inset 0 calc(var(--nxt-hairline) * -2) 0 var(--nxt-brand-bg);
}

/* ── Day backdrop ──────────────────────────────────────────────────────────── */
.nx-time-bar__off {
  background-image: var(--time-bar-hatch);
}
.nx-time-bar__week-line {
  justify-self: start;
  width: 0;
  border-left: var(--nxt-hairline) dashed var(--nxt-border);
}
/* Over the group bands, under the bars. */
.nx-time-bar__today {
  z-index: var(--nxt-z-raised);
  justify-self: center;
  width: calc(var(--nxt-hairline) * 2);
  background: var(--nxt-brand-bg);
  pointer-events: none;
}

/* ── Row and group headers ─────────────────────────────────────────────────── */
.nx-time-bar__row-head,
.nx-time-bar__group-head {
  position: sticky;
  left: 0;
  z-index: calc(var(--nxt-z-raised) + 2);
  grid-column: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-width: 0;
  padding: var(--nxt-space-2xs) var(--nxt-space-sm);
  border-right: var(--nxt-hairline) solid var(--nxt-border);
  border-bottom: var(--nxt-hairline) solid var(--nxt-border-subtle);
}
.nx-time-bar__row-head {
  background: var(--nxt-surface-bg);
}
.nx-time-bar__row-line {
  display: flex;
  align-items: center;
  gap: var(--nxt-space-2xs);
  min-width: 0;
}
.nx-time-bar__tag {
  flex-shrink: 0;
  font-variant-numeric: tabular-nums;
}
.nx-time-bar__row-label,
.nx-time-bar__group-label {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.nx-time-bar__row-label {
  font-size: var(--nxt-font-size-sm);
  font-weight: var(--nxt-font-weight-medium);
}
.nx-time-bar__sub {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: var(--nxt-font-size-xs);
  font-weight: var(--nxt-font-weight-regular);
  color: var(--nxt-fg-subtle);
}

.nx-time-bar__track {
  grid-column: 2 / -1;
  border-bottom: var(--nxt-hairline) solid var(--nxt-border-subtle);
}
.nx-time-bar__row:hover > .nx-time-bar__row-head {
  background: var(--nxt-table-row-hover-bg);
}
/* See-through, so the hatched days stay visible under the hover. */
.nx-time-bar__row:hover > .nx-time-bar__track:not(.is-group) {
  background: color-mix(in srgb, var(--nxt-table-row-hover-bg) 55%, transparent);
}

/* A phase is a quiet band across the chart (the table family's zebra tint). */
.nx-time-bar__group-head,
.nx-time-bar__track.is-group {
  background: var(--nxt-table-zebra-bg);
  border-top: var(--nxt-hairline) solid var(--nxt-border);
  border-bottom: var(--nxt-hairline) solid var(--nxt-border);
}
.nx-time-bar__group-head.has-accent {
  box-shadow: inset calc(var(--nxt-hairline) * 3) 0 0 var(--time-bar-fill);
}
.nx-time-bar__group-main {
  display: flex;
  align-items: center;
  gap: var(--nxt-space-2xs);
  min-width: 0;
  width: 100%;
  color: var(--nxt-fg);
}
.nx-time-bar__group-main.is-toggle {
  margin: 0;
  padding: 0;
  border: none;
  border-radius: var(--nxt-radius-sm);
  background: none;
  font: inherit;
  text-align: start;
  cursor: pointer;
}
.nx-time-bar__group-main.is-toggle:focus-visible {
  outline: calc(var(--nxt-hairline) * 2) solid var(--nxt-focus-ring);
  outline-offset: calc(var(--nxt-hairline) * 2);
}
.nx-time-bar__chevron {
  flex-shrink: 0;
  color: var(--nxt-fg-subtle);
}
.nx-time-bar__group-text {
  display: flex;
  flex-direction: column;
  min-width: 0;
}
.nx-time-bar__group-label {
  font-size: var(--nxt-font-size-xs);
  font-weight: var(--nxt-font-weight-semibold);
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

/* ── Colours: intent tones, or a category hue (`hueVars`) that wins ─────────── */
.tone-brand {
  --time-bar-fill: var(--nxt-brand-bg);
  --time-bar-on: var(--nxt-brand-fg);
}
.tone-neutral {
  --time-bar-fill: var(--nxt-neutral-bg);
  --time-bar-on: var(--nxt-neutral-fg);
}
.tone-info {
  --time-bar-fill: var(--nxt-info-bg);
  --time-bar-on: var(--nxt-info-fg);
}
.tone-success {
  --time-bar-fill: var(--nxt-success-bg);
  --time-bar-on: var(--nxt-success-fg);
}
.tone-warning {
  --time-bar-fill: var(--nxt-warning-bg);
  --time-bar-on: var(--nxt-warning-fg);
}
.tone-danger {
  --time-bar-fill: var(--nxt-danger-bg);
  --time-bar-on: var(--nxt-danger-fg);
}
.has-hue {
  --time-bar-fill: var(--hue);
  --time-bar-on: var(--hue-on);
}

/* ── Bars ──────────────────────────────────────────────────────────────────── */
.nx-time-bar__item {
  position: relative;
  z-index: calc(var(--nxt-z-raised) + 1);
  align-self: center;
  display: flex;
  align-items: center;
  gap: var(--nxt-space-2xs);
  min-width: 0;
  height: calc(var(--nxt-size-unit) * 1.375);
  margin: 0 var(--nxt-hairline);
  padding: 0 var(--nxt-space-xs);
  border: none;
  border-radius: var(--nxt-radius-sm);
  background: var(--time-bar-fill);
  color: var(--time-bar-on);
  box-shadow: var(--nxt-shadow-sm);
  font: inherit;
  font-weight: var(--nxt-font-weight-semibold);
  text-align: start;
  cursor: pointer;
  transition:
    background-color var(--nxt-motion-fast) var(--nxt-ease-out),
    box-shadow var(--nxt-motion-fast) var(--nxt-ease-out);
}
/* The hit area covers the lane, not only the painted bar. */
.nx-time-bar__item::after {
  content: '';
  position: absolute;
  inset: calc(var(--nxt-space-2xs) * -1) 0;
}
.nx-time-bar__item:hover {
  background: color-mix(in srgb, var(--time-bar-fill) 85%, var(--nxt-fg));
  box-shadow: var(--nxt-shadow-md);
}
.nx-time-bar__item:focus-visible {
  outline: calc(var(--nxt-hairline) * 2) solid var(--nxt-focus-ring);
  outline-offset: calc(var(--nxt-hairline) * 2);
}
/* „Verzug“: a red frame with a gap, a shape cue on top of the colour. */
.nx-time-bar__item.is-delayed::before {
  content: '';
  position: absolute;
  inset: calc(var(--nxt-hairline) * -3);
  border: calc(var(--nxt-hairline) * 2) solid var(--nxt-danger-bg);
  border-radius: calc(var(--nxt-radius-sm) + var(--nxt-hairline) * 3);
  pointer-events: none;
}
.nx-time-bar__item.is-delayed:focus-visible {
  outline-offset: calc(var(--nxt-hairline) * 4);
}
/* Cut at the edge of the range: square there, an arrow says it goes on. */
.nx-time-bar__item.is-clipped-start,
.nx-time-bar__item.is-clipped-start::before,
.nx-time-bar__summary.is-clipped-start {
  border-start-start-radius: 0;
  border-end-start-radius: 0;
}
.nx-time-bar__item.is-clipped-end,
.nx-time-bar__item.is-clipped-end::before,
.nx-time-bar__summary.is-clipped-end {
  border-start-end-radius: 0;
  border-end-end-radius: 0;
}
.nx-time-bar__item-text {
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.nx-time-bar__cut {
  flex-shrink: 0;
}

/* The span of a group, under its header. */
.nx-time-bar__summary {
  position: relative;
  z-index: calc(var(--nxt-z-raised) + 1);
  align-self: center;
  height: calc(var(--nxt-size-unit) * 0.375);
  margin: 0 var(--nxt-hairline);
  border-radius: var(--nxt-radius-full);
  background: var(--time-bar-fill);
}

/* ── Milestones ────────────────────────────────────────────────────────────── */
.nx-time-bar__milestone {
  --time-bar-mark: var(--nxt-fg);
  position: relative;
  z-index: calc(var(--nxt-z-raised) + 1);
  align-self: center;
  justify-self: center;
  display: grid;
  place-items: center;
  width: var(--nxt-control-size-xs);
  height: var(--nxt-control-size-xs);
  padding: 0;
  border: none;
  border-radius: var(--nxt-radius-sm);
  background: none;
  cursor: pointer;
}
.nx-time-bar__milestone::before {
  content: '';
  width: calc(var(--nxt-size-unit) * 0.75);
  height: calc(var(--nxt-size-unit) * 0.75);
  border-radius: calc(var(--nxt-radius-sm) / 2);
  background: var(--time-bar-mark);
  box-shadow: var(--nxt-shadow-sm);
  transform: rotate(45deg);
  transition: transform var(--nxt-motion-fast) var(--nxt-ease-out);
}
.nx-time-bar__milestone:hover::before {
  transform: rotate(45deg) scale(1.15);
}
.nx-time-bar__milestone:focus-visible {
  outline: calc(var(--nxt-hairline) * 2) solid var(--nxt-focus-ring);
  outline-offset: 0;
}
/* Missed: red, and ringed so it does not rely on the colour alone. */
.nx-time-bar__milestone.is-missed {
  --time-bar-mark: var(--nxt-danger-bg);
}
.nx-time-bar__milestone.is-missed::before {
  box-shadow:
    0 0 0 var(--nxt-hairline) var(--nxt-surface-bg),
    0 0 0 calc(var(--nxt-hairline) * 3) var(--nxt-danger-bg);
}
.nx-time-bar__milestone.is-done {
  --time-bar-mark: var(--nxt-success-bg);
}

/* ── Empty state ───────────────────────────────────────────────────────────── */
.nx-time-bar__empty {
  grid-column: 1 / -1;
}
.nx-time-bar__empty-inner {
  position: sticky;
  left: 0;
  width: fit-content;
  padding: 0 var(--nxt-space-md);
}

/* ── Legend ────────────────────────────────────────────────────────────────── */
.nx-time-bar__legend {
  display: flex;
  flex-wrap: wrap;
  gap: var(--nxt-space-2xs) var(--nxt-space-md);
  margin: 0;
  padding: 0;
  list-style: none;
  font-size: var(--nxt-font-size-xs);
  color: var(--nxt-fg-subtle);
}
.nx-time-bar__legend li {
  display: inline-flex;
  align-items: center;
  gap: var(--nxt-space-xs);
}
.nx-time-bar__swatch {
  flex-shrink: 0;
  width: calc(var(--nxt-size-unit) * 0.75);
  height: calc(var(--nxt-size-unit) * 0.75);
  border-radius: var(--nxt-radius-sm);
}
.nx-time-bar__swatch.kind-bar {
  background: var(--time-bar-fill);
}
.nx-time-bar__swatch.kind-delay {
  background: var(--nxt-surface-bg);
  box-shadow: inset 0 0 0 calc(var(--nxt-hairline) * 2) var(--nxt-danger-bg);
}
.nx-time-bar__swatch.kind-milestone,
.nx-time-bar__swatch.kind-milestone-missed,
.nx-time-bar__swatch.kind-milestone-done {
  width: calc(var(--nxt-size-unit) * 0.625);
  height: calc(var(--nxt-size-unit) * 0.625);
  border-radius: calc(var(--nxt-radius-sm) / 2);
  background: var(--nxt-fg);
  transform: rotate(45deg);
}
.nx-time-bar__swatch.kind-milestone-missed {
  background: var(--nxt-danger-bg);
}
.nx-time-bar__swatch.kind-milestone-done {
  background: var(--nxt-success-bg);
}
.nx-time-bar__swatch.kind-today {
  width: calc(var(--nxt-hairline) * 2);
  border-radius: 0;
  background: var(--nxt-brand-bg);
}
.nx-time-bar__swatch.kind-non-working {
  border: var(--nxt-hairline) solid var(--nxt-border);
  background-image: var(--time-bar-hatch);
}

.nx-time-bar__sr-only {
  position: absolute;
  width: var(--nxt-hairline);
  height: var(--nxt-hairline);
  margin: calc(var(--nxt-hairline) * -1);
  overflow: hidden;
  clip-path: inset(50%);
  white-space: nowrap;
}

@media (prefers-reduced-motion: reduce) {
  .nx-time-bar__item,
  .nx-time-bar__milestone::before {
    transition: none;
  }
  .nx-time-bar__milestone:hover::before {
    transform: rotate(45deg);
  }
}
/* The same where a subtree asks for less motion: data-nx-motion="reduced"
   on an ancestor (reset.css does the durations). */
:where([data-nx-motion='reduced']) .nx-time-bar__item,
:where([data-nx-motion='reduced']) .nx-time-bar__milestone::before {
  transition: none;
}
:where([data-nx-motion='reduced']) .nx-time-bar__milestone:hover::before {
  transform: rotate(45deg);
}

/* Forced colours drop fills: marks keep a system-colour outline instead. */
@media (forced-colors: active) {
  .nx-time-bar__item,
  .nx-time-bar__summary,
  .nx-time-bar__milestone::before {
    border: calc(var(--nxt-hairline) * 2) solid ButtonText;
  }
  .nx-time-bar__item.is-delayed::before,
  .nx-time-bar__milestone.is-missed::before {
    border-color: Highlight;
  }
  .nx-time-bar__today {
    forced-color-adjust: none;
    background: Highlight;
  }
}
</style>
